import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { Modal } from "components/common/Modal";
import {
  TabsList,
  TabsPanel,
  TabsPanels,
  TabsRoot,
  TabsTrigger,
} from "components/common/Tabs";
import { ComparedPlanEmptyState } from "./ComparedPlanEmptyState";
import { PlanComparisonHeader } from "./PlanComparisonHeader";
import { QuantitiesTab } from "./QuantitiesTab";
import { VisualDiffTab } from "./VisualDiffTab";
import { useComparedPlanSelection } from "./hooks/useComparedPlanSelection";
import type { PlanComparisonModalProps, PlanComparisonTab } from "./types";

// The plan whose menu was opened is "before", the one picked in the dropdown is "after".
export const PlanComparisonModal = ({
  basePlanRecord,
  planRecords,
  onClose,
}: PlanComparisonModalProps) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<PlanComparisonTab>("quantities");

  const { candidateRecords, comparedPlanRecord, selectComparedPlan } =
    useComparedPlanSelection({ basePlanRecord, planRecords });

  const handleTabChange = useCallback(
    (nextTab: PlanComparisonTab) => {
      if (nextTab !== activeTab) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_COMPARISON_TAB_CHANGED, {
          tab: nextTab,
        });
      }
      setActiveTab(nextTab);
    },
    [activeTab],
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="full"
      className="flex h-[90vh] flex-col p-0"
    >
      <PlanComparisonHeader
        beforePlanName={basePlanRecord.name}
        candidateRecords={candidateRecords}
        afterPlanRecord={comparedPlanRecord}
        onAfterPlanSelect={selectComparedPlan}
        onClose={onClose}
      />
      {comparedPlanRecord === null ? (
        <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-4 sm:px-6">
          <ComparedPlanEmptyState
            candidateRecords={candidateRecords}
            onComparedPlanSelect={selectComparedPlan}
          />
        </div>
      ) : (
        <TabsRoot
          value={activeTab}
          onValueChange={handleTabChange}
          styles={{ variant: "underline" }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList className="px-4 sm:px-6">
            <TabsTrigger value="quantities">
              {t("smartSurveyor.planComparison.tabs.quantities")}
            </TabsTrigger>
            <TabsTrigger value="visual">
              {t("smartSurveyor.planComparison.tabs.visual")}
            </TabsTrigger>
          </TabsList>
          <TabsPanels className="flex min-h-0 flex-1 flex-col">
            <TabsPanel
              value="quantities"
              className="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-6"
            >
              <QuantitiesTab
                beforePlanRecord={basePlanRecord}
                afterPlanRecord={comparedPlanRecord}
              />
            </TabsPanel>
            <TabsPanel
              value="visual"
              className="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-6"
            >
              {/* Remounts on a new compared plan so no selection outlives the diff it came from. */}
              <VisualDiffTab
                key={comparedPlanRecord.id}
                beforePlanRecord={basePlanRecord}
                afterPlanRecord={comparedPlanRecord}
              />
            </TabsPanel>
          </TabsPanels>
        </TabsRoot>
      )}
    </Modal>
  );
};
